import { useCallback } from 'react';
import { noop } from 'lodash';
import { useRouter } from 'next/navigation';
import { updateQuery } from '@frameio/graphql-client';
import type {
  Account,
  DuplicateProjectInput,
  DuplicateProjectMutationResult,
  GetImageUploadQueryResult,
  Project,
} from '@frameio/graphql-client/generated';
import {
  GetAccountProjects,
  GetWorkspaceProjects,
  useDuplicateProjectMutation,
  useGetImageUploadLazyQuery,
} from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { ToastButton } from '@frameio/vapor';
import { getLink } from '../../../routing';
import { useTrack } from '../../../segment/hooks';
import { WORKSPACE_PROJECTS_PAGE_SIZE } from '../../constants';
import { MOUNTED_STORAGE_ACTIVITY_WINDOW_DAYS } from '../../mounted-storage/constants';
import { useLoadingToast } from '../../shared';
import { incrementProjectCountForWorkspaceInfoBasic } from '../cache-ops';
import { uploadProjectImage } from '../utils';
import TEXT from './generated/TEXT/Shared_UseDuplicateProject';

type DuplicateProjectOpts = {
  onError?: () => void;
  onSuccess?: (
    project: NonNullable<
      NonNullable<DuplicateProjectMutationResult['data']>['duplicateProject']
    >['project']
  ) => void;
};

type DuplicateProjectReturn = (
  input: DuplicateProjectInput,
  accountId: Account['id'],
  blob?: Blob | null,
  deleteAvatar?: boolean
) => void;

const DEFAULT_OPTS: DuplicateProjectOpts = {
  onError: noop,
  onSuccess: noop,
};

/**
 * The useDuplicateProject hook can be used to duplicate a project. Adding an avatar
 * image requires providing an imageUploadUrl and a blob for the avatar.
 *
 * @param opts.onError an optional callback fired if the duplicate fails
 * @param opts.onSuccess an optional callback fired if the duplicate succeeds
 * @returns a function to call when ready to duplicate the project
 */
export function useDuplicateProject(
  opts?: DuplicateProjectOpts
): DuplicateProjectReturn {
  const { onError, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [duplicateProject] = useDuplicateProjectMutation();
  const [imageUploadQuery] = useGetImageUploadLazyQuery({
    fetchPolicy: 'network-only',
  });
  const { addToast, updateAndHideToast } = useLoadingToast();
  const track = useTrack();
  const router = useRouter();

  return useCallback(
    /**
     * @param details the DuplicateProjectInput values including workspaceId, name
     *              and image (optional)
     * @param accountId the current user's active account ID
     * @param blob Blob instance for the avatar
     */
    async (details, accountId, blob = undefined, deleteAvatar) => {
      const input = {
        ...details,
      };
      let imageUploadResult: GetImageUploadQueryResult;
      let imageUploadUrl: string;
      const toastId = addToast(TEXT.loading);

      track(
        'project-duplicated',
        {
          project_id: details.sourceProjectId,
          with_assets: details.assetsToCopy !== 'NONE',
        },
        { logToDatadog: true }
      );

      if (blob && !deleteAvatar) {
        imageUploadResult = await imageUploadQuery({
          variables: {
            mimeType: blob.type,
            resourceType: 'PROJECT',
          },
        });

        // eslint-disable-next-line no-constant-condition
        if (imageUploadResult?.data?.imageUpload?.url) {
          imageUploadUrl = imageUploadResult.data.imageUpload.url;
          input.image = {
            id: imageUploadResult.data.imageUpload.id,
            type: blob.type,
          };
          uploadProjectImage(imageUploadUrl, blob, noop);
        }
      } else if (deleteAvatar) {
        input.image = null;
      }

      const handleReject = () => {
        updateAndHideToast(toastId, {
          title: TEXT.errorMessage,
          variant: 'error',
        });
        onError?.();
      };

      const handleResolve = (result: DuplicateProjectMutationResult) => {
        const newProject = result.data?.duplicateProject?.project;

        if (newProject) {
          updateAndHideToast(toastId, {
            adornmentAfter: (dismiss) => (
              <ToastButton
                aria-label={TEXT.viewAriaLabel}
                onPress={() => {
                  dismiss?.();
                  router.push(
                    getLink({
                      projectId: newProject.id,
                      page: 'project',
                    })
                  );
                }}
              >
                {TEXT.view}
              </ToastButton>
            ),
            title: TEXT.successMessage,
            variant: 'success',
          });
          onSuccess?.(newProject);
        } else {
          handleReject();
        }
      };

      duplicateProject({
        variables: {
          input: {
            ...input,
          },
        },
        update(proxy, result) {
          const newProject = result?.data?.duplicateProject?.project;

          if (!newProject) return;

          incrementProjectCountForWorkspaceInfoBasic({
            cache: proxy,
            destinationWorkspaceId: input.destinationWorkspaceId,
            newProject: newProject as Project,
            accountId,
          });

          updateQuery(
            proxy,
            GetWorkspaceProjects,
            {
              workspaceId: input.destinationWorkspaceId,
              page: {
                first: WORKSPACE_PROJECTS_PAGE_SIZE,
              },
            },
            (draft) => {
              if (!draft) return;

              draft.account?.workspace?.projects?.nodes.push(newProject);
            }
          );

          updateQuery(
            proxy,
            GetAccountProjects,
            {
              accountId,
              page: {
                first: DEFAULT_INITIAL_PAGE_SIZE,
              },
              mountedStorageActivityDays: MOUNTED_STORAGE_ACTIVITY_WINDOW_DAYS,
            },
            (draft) => {
              if (!draft?.account?.projects) return;
              draft.account.projects.nodes.unshift(newProject);
              draft.account.projects.totalCount += 1;
            }
          );
        },
      }).then(handleResolve, handleReject);
    },
    [
      addToast,
      track,
      duplicateProject,
      imageUploadQuery,
      updateAndHideToast,
      onError,
      onSuccess,
      router,
    ]
  );
}

TEXT.DEFINE({
  /** Toast message while duplicating project */
  loading: 'Duplicating Project',
  /** Toast message when project duplication fails */
  errorMessage: "Couldn't duplicate Project",
  /** Toast message when project is successfully duplicated */
  successMessage: 'New Project created',
  /** Toast action label for viewing the project created by duplication */
  view: 'View',
  /** Aria label for viewing the project created by duplication */
  viewAriaLabel: 'View duplicated project',
});
